import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { Button, Heading, Text, Badge, Flex } from '@radix-ui/themes';
import api from '../../utils/api';
import { usePagination } from '../../utils/usePagination';
import Pagination from '../shared/Pagination';
import PlanLimitCallout from '../Billing/PlanLimitCallout';
import { planLimitFromError, type PlanLimitDetails } from '../../utils/billing';
import './QuizList.css';

interface Quiz {
  id: number;
  name: string;
  theme: string | null;
  isTemplate: boolean;
  createdAt: string;
  updatedAt: string;
  _count: {
    rounds: number;
  };
}

interface Venue {
  id: number;
  name: string;
}

interface DocImportResult {
  quizId: string;
  quizName: string;
  roundCount: number;
  questionCount: number;
  warnings: string[];
}

interface SheetImportResult {
  eventId: string;
  pin: string;
  teamCount: number;
  warnings: string[];
}

interface QuizzesResponse {
  status: string;
  data: {
    quizzes: Quiz[];
  };
}

interface SearchQuizRef {
  id: string;
  name: string;
  lastPlayed: string | null;
}

interface SearchResults {
  questions: {
    id: string;
    text: string;
    answer: string;
    type: string;
    round: { id: string; title: string; quiz: SearchQuizRef };
  }[];
  rounds: {
    id: string;
    title: string;
    theme: string | null;
    roundType: string;
    _count: { questions: number };
    quiz: SearchQuizRef;
  }[];
}

// "Played 3 weeks ago" / "Never played" — the answer the host is actually after.
export function lastPlayedLabel(date: string | null): string {
  if (!date) return 'Never played';
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  if (days < 0) return `Scheduled ${new Date(date).toLocaleDateString()}`;
  if (days === 0) return 'Played today';
  if (days === 1) return 'Played yesterday';
  if (days < 60) return `Played ${days} days ago`;
  return `Played ${Math.round(days / 30)} months ago`;
}

const QuizList: React.FC = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search across all quizzes' rounds + questions ("have I used this recently?")
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTheme, setNewTheme] = useState('');
  const [creating, setCreating] = useState(false);

  // Duplicate loading state
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Quiz | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importTab, setImportTab] = useState<'doc' | 'docx' | 'sheet'>('doc');

  // .docx upload import (bold-aware). Shares importQuizName with the Doc tab.
  const [docxFile, setDocxFile] = useState<File | null>(null);
  const [importingDocx, setImportingDocx] = useState(false);
  const [docxImportResult, setDocxImportResult] = useState<DocImportResult | null>(null);

  // Doc import state — URL paste, not Drive search. The service-account-
  // backed /api/import/drive-search couldn't see the user's personal Drive,
  // so we just take a URL (or bare ID) and let the backend extract the id.
  const [docUrl, setDocUrl] = useState('');
  const [importQuizName, setImportQuizName] = useState('');
  const [importingDoc, setImportingDoc] = useState(false);
  const [docImportResult, setDocImportResult] = useState<DocImportResult | null>(null);

  // Sheet import state
  const [sheetUrl, setSheetUrl] = useState('');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [sheetDate, setSheetDate] = useState('');
  const [selectedQuizId, setSelectedQuizId] = useState('');
  const [importingSheet, setImportingSheet] = useState(false);
  const [sheetImportResult, setSheetImportResult] = useState<SheetImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importLimit, setImportLimit] = useState<PlanLimitDetails | null>(null);

  const fetchQuizzes = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get<QuizzesResponse>('/api/quizzes');
      setQuizzes(response.data.data.quizzes);
    } catch (err) {
      setError('Failed to load quizzes. Please try again.');
      console.error('Error fetching quizzes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  // Debounced search. Blank/1-char query drops back to the quiz grid.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get('/api/questions/search', { params: { q } })
        .then((res) => setResults(res.data.data))
        .catch(() => setResults({ questions: [], rounds: [] }))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;

    setCreating(true);
    try {
      const response = await api.post('/api/quizzes', {
        name: newName.trim(),
        theme: newTheme.trim() || undefined,
      });
      const createdQuiz = response.data.data?.quiz || response.data;
      setShowCreateDialog(false);
      setNewName('');
      setNewTheme('');
      navigate(`/quizzes/${createdQuiz.id}/edit`);
    } catch (err) {
      console.error('Error creating quiz:', err);
      setError('Failed to create quiz. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (quizId: number) => {
    if (duplicatingId) return;
    setDuplicatingId(quizId);
    try {
      await api.post(`/api/quizzes/${quizId}/duplicate`);
      await fetchQuizzes();
    } catch (err) {
      console.error('Error duplicating quiz:', err);
      setError('Failed to duplicate quiz. Please try again.');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);
    try {
      await api.delete(`/api/quizzes/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchQuizzes();
    } catch (err) {
      console.error('Error deleting quiz:', err);
      setError('Failed to delete quiz. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // ── Import helpers ──────────────────────────────────────────────────────

  // Extract a Google file ID from a Drive URL like
  //   https://docs.google.com/document/d/<ID>/edit
  // or accept a bare ID. Returns null if the input doesn't look valid.
  const extractDriveId = (input: string): string | null => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const m = trimmed.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (m) return m[1] ?? null;
    // Bare ID: alphanumeric / dash / underscore, ≥20 chars (matches Drive)
    if (/^[a-zA-Z0-9-_]{20,}$/.test(trimmed)) return trimmed;
    return null;
  };

  const docId = extractDriveId(docUrl);
  const sheetId = extractDriveId(sheetUrl);

  // Fetch venues + quizzes when import dialog opens
  useEffect(() => {
    if (!showImportDialog) return;
    api.get('/venues').then((res) => setVenues(res.data)).catch(() => {});
  }, [showImportDialog]);

  const resetImportDialog = () => {
    setImportTab('doc');
    setDocUrl('');
    setImportQuizName('');
    setImportingDoc(false);
    setDocImportResult(null);
    setDocxFile(null);
    setImportingDocx(false);
    setDocxImportResult(null);
    setSheetUrl('');
    setSelectedVenueId('');
    setSheetDate('');
    setSelectedQuizId('');
    setImportingSheet(false);
    setSheetImportResult(null);
    setImportError(null);
  };

  const handleImportDoc = async () => {
    if (!docId || !importQuizName.trim() || importingDoc) return;
    setImportingDoc(true);
    setImportError(null);
    try {
      const res = await api.post('/api/import/google-doc', {
        docId,
        quizName: importQuizName.trim(),
      });
      setDocImportResult(res.data.data);
      await fetchQuizzes();
    } catch (err: any) {
      setImportError(err.response?.data?.message || 'Failed to import document.');
    } finally {
      setImportingDoc(false);
    }
  };

  const handleImportDocx = async () => {
    if (!docxFile || !importQuizName.trim() || importingDocx) return;
    setImportingDocx(true);
    setImportError(null);
    setImportLimit(null);
    try {
      const form = new FormData();
      form.append('file', docxFile);
      form.append('quizName', importQuizName.trim());
      const res = await api.post('/api/import/docx', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocxImportResult(res.data.data);
      await fetchQuizzes();
    } catch (err: any) {
      const limit = planLimitFromError(err);
      if (limit) {
        setImportLimit(limit);
      } else {
        setImportError(err.response?.data?.message || 'Failed to import document.');
      }
    } finally {
      setImportingDocx(false);
    }
  };

  const handleImportSheet = async () => {
    if (!sheetId || !selectedVenueId || !sheetDate || importingSheet) return;
    setImportingSheet(true);
    setImportError(null);
    try {
      const res = await api.post('/api/import/google-sheet', {
        sheetUrl: `https://docs.google.com/spreadsheets/d/${sheetId}`,
        venueId: selectedVenueId,
        date: sheetDate,
        quizId: selectedQuizId || undefined,
      });
      setSheetImportResult(res.data.data);
    } catch (err: any) {
      setImportError(err.response?.data?.message || 'Failed to import sheet.');
    } finally {
      setImportingSheet(false);
    }
  };

  const qp = usePagination(quizzes, 12);

  return (
    <div className="quiz-list-page">
      <div className="quiz-list-header">
        <Heading as="h1" size="6" className="quiz-list-heading">Quizzes</Heading>
        <div className="quiz-list-header-actions">
          <Button className="btn-create btn-import" variant="outline" onClick={() => { resetImportDialog(); setShowImportDialog(true); }}>
            Import
          </Button>
          <Button className="btn-create" onClick={() => setShowCreateDialog(true)}>
            + Create Quiz
          </Button>
        </div>
      </div>

      {error && <Text className="quiz-list-error">{error}</Text>}

      <input
        className="quiz-search-input"
        type="search"
        placeholder="Search rounds & questions — have I used this before?"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {results ? (
        <div className="quiz-search-results">
          {searching && <Text size="1" className="quiz-card-meta-text">Searching…</Text>}
          {results.rounds.length === 0 && results.questions.length === 0 && !searching && (
            <Text size="2">No rounds or questions match &ldquo;{query.trim()}&rdquo;.</Text>
          )}

          {results.rounds.length > 0 && (
            <>
              <Heading as="h2" size="3" mt="3" mb="2">Rounds ({results.rounds.length})</Heading>
              {results.rounds.map((r) => (
                <div key={r.id} className="quiz-search-hit" onClick={() => navigate(`/quizzes/${r.quiz.id}/edit`)}>
                  <Text size="2" weight="bold">{r.title}</Text>
                  {r.theme && <Text size="1" className="quiz-card-meta-text"> — {r.theme}</Text>}
                  <Text size="1" className="quiz-card-meta-text" as="p">
                    {r.quiz.name} · {r._count.questions} question{r._count.questions !== 1 ? 's' : ''} · {lastPlayedLabel(r.quiz.lastPlayed)}
                  </Text>
                </div>
              ))}
            </>
          )}

          {results.questions.length > 0 && (
            <>
              <Heading as="h2" size="3" mt="4" mb="2">Questions ({results.questions.length})</Heading>
              {results.questions.map((qu) => (
                <div key={qu.id} className="quiz-search-hit" onClick={() => navigate(`/quizzes/${qu.round.quiz.id}/edit`)}>
                  <Text size="2">{qu.text}</Text>
                  <Text size="1" className="quiz-card-meta-text" as="p">
                    A: {qu.answer} · {qu.round.quiz.name} / {qu.round.title} · {lastPlayedLabel(qu.round.quiz.lastPlayed)}
                  </Text>
                </div>
              ))}
            </>
          )}
        </div>
      ) : loading ? (
        <div className="quiz-list-loading"><Text size="2">Loading quizzes...</Text></div>
      ) : quizzes.length === 0 ? (
        <div className="quiz-list-empty">
          <Heading as="h2" size="4" className="quiz-list-empty-heading">No quizzes yet</Heading>
          <Text size="2" className="quiz-list-empty-text" as="p">
            A quiz is the reusable content for a trivia night — rounds of questions, plus optional
            song or image questions. Once you have a quiz, you can launch a Live Event for it at any venue.
          </Text>
          <Flex gap="2" mt="3" justify="center">
            <Button onClick={() => setShowCreateDialog(true)}>+ Create your first quiz</Button>
            <Button variant="soft" onClick={() => { resetImportDialog(); setShowImportDialog(true); }}>
              Import from Google Doc
            </Button>
          </Flex>
        </div>
      ) : (
        <>
        <div className="quiz-grid">
          {qp.slice.map((quiz) => (
            <div key={quiz.id} className="quiz-card">
              <div className="quiz-card-header">
                <Heading as="h3" size="3" className="quiz-card-name">{quiz.name}</Heading>
                <div className="quiz-card-badges">
                  {quiz.theme && (
                    <Badge className="badge badge-theme" variant="soft">{quiz.theme}</Badge>
                  )}
                  {quiz.isTemplate && (
                    <Badge className="badge badge-template" variant="soft">Template</Badge>
                  )}
                </div>
              </div>

              <div className="quiz-card-meta">
                <Text size="1" className="quiz-card-meta-text">{quiz._count.rounds} round{quiz._count.rounds !== 1 ? 's' : ''}</Text>
                <Text size="1" className="quiz-card-meta-text">Created {formatDate(quiz.createdAt)}</Text>
              </div>

              <div className="quiz-card-actions">
                <Button
                  variant="outline"
                  className="btn-action btn-edit"
                  onClick={() => navigate(`/quizzes/${quiz.id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  className="btn-action btn-duplicate"
                  onClick={() => handleDuplicate(quiz.id)}
                  disabled={duplicatingId === quiz.id}
                >
                  {duplicatingId === quiz.id ? '...' : 'Duplicate'}
                </Button>
                <Button
                  variant="outline"
                  className="btn-action btn-delete"
                  onClick={() => setDeleteTarget(quiz)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Pagination page={qp.page} pageCount={qp.pageCount} total={qp.total} onPrev={qp.prev} onNext={qp.next} label="quizzes" />
        </>
      )}

      {/* Create Quiz Dialog */}
      <Dialog.Root open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog-content">
            <Dialog.Title asChild>
              <Heading as="h2" size="4" className="dialog-heading">Create Quiz</Heading>
            </Dialog.Title>
            <form onSubmit={handleCreate}>
              <div className="dialog-field">
                <label htmlFor="quiz-name">Name</label>
                <input
                  id="quiz-name"
                  type="text"
                  placeholder="e.g. Tuesday Night Trivia"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="dialog-field">
                <label htmlFor="quiz-theme">Theme (optional)</label>
                <input
                  id="quiz-theme"
                  type="text"
                  placeholder="e.g. 90s Pop Culture"
                  value={newTheme}
                  onChange={(e) => setNewTheme(e.target.value)}
                />
              </div>
              <div className="dialog-actions">
                <Dialog.Close asChild>
                  <Button variant="outline" className="btn-cancel" type="button">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  type="submit"
                  className="btn-submit"
                  disabled={!newName.trim() || creating}
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <AlertDialog.Root open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="dialog-overlay" />
          <AlertDialog.Content className="dialog-content confirm-dialog">
            <AlertDialog.Title asChild>
              <Heading as="h2" size="4" className="dialog-heading">Delete Quiz</Heading>
            </AlertDialog.Title>
            <AlertDialog.Description asChild>
              <Text size="2" className="dialog-description-text">
                Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? This action cannot
                be undone.
              </Text>
            </AlertDialog.Description>
            <div className="dialog-actions">
              <AlertDialog.Cancel asChild>
                <Button variant="outline" className="btn-cancel">
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  className="btn-delete-confirm"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Import Dialog */}
      <Dialog.Root open={showImportDialog} onOpenChange={(open) => { if (!open) resetImportDialog(); setShowImportDialog(open); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog-content import-dialog">
            <Dialog.Title asChild>
              <Heading as="h2" size="4" className="dialog-heading">Import a quiz</Heading>
            </Dialog.Title>

            {/* Tab toggle */}
            <div className="import-tabs">
              <button
                className={`import-tab ${importTab === 'doc' ? 'import-tab-active' : ''}`}
                onClick={() => setImportTab('doc')}
                type="button"
              >
                Quiz Doc
              </button>
              <button
                className={`import-tab ${importTab === 'docx' ? 'import-tab-active' : ''}`}
                onClick={() => setImportTab('docx')}
                type="button"
              >
                Word Doc
              </button>
              <button
                className={`import-tab ${importTab === 'sheet' ? 'import-tab-active' : ''}`}
                onClick={() => setImportTab('sheet')}
                type="button"
              >
                Score Sheet
              </button>
            </div>

            {importError && <Text size="2" className="import-error">{importError}</Text>}
            {importLimit && <PlanLimitCallout details={importLimit} />}

            {/* ─── Tab 1: Quiz Doc ─── */}
            {importTab === 'doc' && !docImportResult && (
              <div className="import-tab-content">
                <div className="dialog-field">
                  <label htmlFor="doc-url">Google Doc URL</label>
                  <input
                    id="doc-url"
                    type="text"
                    placeholder="https://docs.google.com/document/d/..."
                    value={docUrl}
                    onChange={(e) => setDocUrl(e.target.value)}
                    autoFocus
                  />
                  <Text size="1" className="import-status" style={{ marginTop: 4, color: '#666' }}>
                    The doc must be shared with the service account so the
                    importer can read it.
                  </Text>
                  {docUrl.trim() && !docId && (
                    <Text size="1" className="import-status" style={{ marginTop: 4, color: '#b91c1c' }}>
                      Doesn&apos;t look like a valid Google Doc URL or ID.
                    </Text>
                  )}
                </div>

                <div className="dialog-field">
                  <label htmlFor="import-quiz-name">Quiz Name</label>
                  <input
                    id="import-quiz-name"
                    type="text"
                    placeholder="e.g. May 21st Sci-Fi Trivia"
                    value={importQuizName}
                    onChange={(e) => setImportQuizName(e.target.value)}
                  />
                </div>

                <div className="dialog-actions">
                  <Dialog.Close asChild>
                    <Button variant="outline" className="btn-cancel" type="button">Cancel</Button>
                  </Dialog.Close>
                  <Button
                    className="btn-submit"
                    disabled={!docId || !importQuizName.trim() || importingDoc}
                    onClick={handleImportDoc}
                  >
                    {importingDoc ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            )}

            {/* Doc import success */}
            {importTab === 'doc' && docImportResult && (
              <div className="import-tab-content">
                <div className="import-success">
                  <Text size="2" className="import-success-text">
                    Imported {docImportResult.roundCount} round{docImportResult.roundCount !== 1 ? 's' : ''} and {docImportResult.questionCount} question{docImportResult.questionCount !== 1 ? 's' : ''}.
                  </Text>
                  {docImportResult.warnings.length > 0 && (
                    <div className="import-warnings">
                      {docImportResult.warnings.map((w, i) => (
                        <Text key={i} size="1" className="import-warning-text">{w}</Text>
                      ))}
                    </div>
                  )}
                  <div className="dialog-actions">
                    <Dialog.Close asChild>
                      <Button variant="outline" className="btn-cancel" type="button">Close</Button>
                    </Dialog.Close>
                    <Button
                      className="btn-submit"
                      onClick={() => { setShowImportDialog(false); navigate(`/quizzes/${docImportResult.quizId}/edit`); }}
                    >
                      Open in Builder
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Tab: Word Doc (.docx, bold-aware) ─── */}
            {importTab === 'docx' && !docxImportResult && (
              <div className="import-tab-content">
                <div className="dialog-field">
                  <label htmlFor="docx-file">Word document (.docx)</label>
                  <input
                    id="docx-file"
                    type="file"
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => setDocxFile(e.target.files?.[0] ?? null)}
                  />
                  <Text size="1" className="import-status" style={{ marginTop: 4, color: '#666' }}>
                    Rounds as &ldquo;Round 1: Theme&rdquo; headings, one question per
                    line with the <strong>answer in bold</strong>. In Google Docs:
                    File → Download → Microsoft Word (.docx).
                  </Text>
                </div>

                <div className="dialog-field">
                  <label htmlFor="import-quiz-name-docx">Quiz Name</label>
                  <input
                    id="import-quiz-name-docx"
                    type="text"
                    placeholder="e.g. Nov 19th Trivia"
                    value={importQuizName}
                    onChange={(e) => setImportQuizName(e.target.value)}
                  />
                </div>

                <div className="dialog-actions">
                  <Dialog.Close asChild>
                    <Button variant="outline" className="btn-cancel" type="button">Cancel</Button>
                  </Dialog.Close>
                  <Button
                    className="btn-submit"
                    disabled={!docxFile || !importQuizName.trim() || importingDocx}
                    onClick={handleImportDocx}
                  >
                    {importingDocx ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            )}

            {/* Word Doc import success */}
            {importTab === 'docx' && docxImportResult && (
              <div className="import-tab-content">
                <div className="import-success">
                  <Text size="2" className="import-success-text">
                    Imported {docxImportResult.roundCount} round{docxImportResult.roundCount !== 1 ? 's' : ''} and {docxImportResult.questionCount} question{docxImportResult.questionCount !== 1 ? 's' : ''}.
                  </Text>
                  {docxImportResult.warnings.length > 0 && (
                    <div className="import-warnings">
                      {docxImportResult.warnings.map((w, i) => (
                        <Text key={i} size="1" className="import-warning-text">{w}</Text>
                      ))}
                    </div>
                  )}
                  <div className="dialog-actions">
                    <Dialog.Close asChild>
                      <Button variant="outline" className="btn-cancel" type="button">Close</Button>
                    </Dialog.Close>
                    <Button
                      className="btn-submit"
                      onClick={() => { setShowImportDialog(false); navigate(`/quizzes/${docxImportResult.quizId}/edit`); }}
                    >
                      Open in Builder
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Tab 2: Score Sheet ─── */}
            {importTab === 'sheet' && !sheetImportResult && (
              <div className="import-tab-content">
                <div className="dialog-field">
                  <label htmlFor="sheet-url">Google Sheet URL</label>
                  <input
                    id="sheet-url"
                    type="text"
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={sheetUrl}
                    onChange={(e) => setSheetUrl(e.target.value)}
                    autoFocus
                  />
                  <Text size="1" className="import-status" style={{ marginTop: 4, color: '#666' }}>
                    The sheet must be shared with the service account.
                  </Text>
                  {sheetUrl.trim() && !sheetId && (
                    <Text size="1" className="import-status" style={{ marginTop: 4, color: '#b91c1c' }}>
                      Doesn&apos;t look like a valid Google Sheet URL or ID.
                    </Text>
                  )}
                </div>

                <div className="dialog-field">
                  <label htmlFor="import-venue">Venue</label>
                  <select
                    id="import-venue"
                    className="import-select"
                    value={selectedVenueId}
                    onChange={(e) => setSelectedVenueId(e.target.value)}
                  >
                    <option value="">Select a venue</option>
                    {venues.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>

                <div className="dialog-field">
                  <label htmlFor="import-date">Date</label>
                  <input
                    id="import-date"
                    type="date"
                    value={sheetDate}
                    onChange={(e) => setSheetDate(e.target.value)}
                  />
                </div>

                <div className="dialog-field">
                  <label htmlFor="import-quiz-link">Link to Quiz (optional)</label>
                  <select
                    id="import-quiz-link"
                    className="import-select"
                    value={selectedQuizId}
                    onChange={(e) => setSelectedQuizId(e.target.value)}
                  >
                    <option value="">None</option>
                    {quizzes.map((q) => (
                      <option key={q.id} value={q.id}>{q.name}</option>
                    ))}
                  </select>
                </div>

                <div className="dialog-actions">
                  <Dialog.Close asChild>
                    <Button variant="outline" className="btn-cancel" type="button">Cancel</Button>
                  </Dialog.Close>
                  <Button
                    className="btn-submit"
                    disabled={!sheetId || !selectedVenueId || !sheetDate || importingSheet}
                    onClick={handleImportSheet}
                  >
                    {importingSheet ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            )}

            {/* Sheet import success */}
            {importTab === 'sheet' && sheetImportResult && (
              <div className="import-tab-content">
                <div className="import-success">
                  <Text size="2" className="import-success-text">
                    Imported {sheetImportResult.teamCount} team{sheetImportResult.teamCount !== 1 ? 's' : ''}.
                  </Text>
                  {sheetImportResult.warnings.length > 0 && (
                    <div className="import-warnings">
                      {sheetImportResult.warnings.map((w, i) => (
                        <Text key={i} size="1" className="import-warning-text">{w}</Text>
                      ))}
                    </div>
                  )}
                  <div className="dialog-actions">
                    <Dialog.Close asChild>
                      <Button variant="outline" className="btn-cancel" type="button">Close</Button>
                    </Dialog.Close>
                    <Button
                      className="btn-submit"
                      onClick={() => { setShowImportDialog(false); navigate(`/events/${sheetImportResult.eventId}`); }}
                    >
                      View Scores
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default QuizList;
